import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { downloadReport } from "./exportHelpers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { FileText, FileDown, FileSpreadsheet, Loader2 } from "lucide-react";

const C = { burg: "#8B1E1E", gold: "#C49A4A", text: "#3B2722", muted: "#8B7355", border: "#EFE4D0", canvas: "#FAF6EC", green: "#2E7D32", red: "#C62828" };
const TABS = [
  { key: "pnl", label: "Trading & Profit/Loss", ep: "/reports/statements/pnl" },
  { key: "funds-release", label: "Funds Release", ep: "/reports/statements/funds-release" },
  { key: "revenue", label: "Daily Revenue", ep: "/reports/hub/revenue" },
  { key: "inventory", label: "Inventory Valuation", ep: "/reports/hub/inventory" },
  { key: "expenses", label: "Expense Analysis", ep: "/reports/hub/expenses" },
];
const n = (v) => (v || v === 0 ? "₦" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—");
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Row({ label, value, bold, indent, color }) {
  return (
    <div className="flex justify-between py-1.5 px-3" style={{ borderBottom: `1px solid ${C.border}`, background: bold ? "#FDF6E3" : "transparent" }}>
      <span style={{ color: color || C.text, fontWeight: bold ? 700 : 400, paddingLeft: indent ? 16 : 0 }}>{label}</span>
      <span className="tabular-nums" style={{ color: color || C.text, fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}

export default function StatementsHub() {
  const [tab, setTab] = useState("pnl");
  const [period, setPeriod] = useState(thisMonth());
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("all");
  const [data, setData] = useState(null);
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/locations").then(setLocations).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true); setData(null);
    const ep = TABS.find((t) => t.key === tab).ep;
    const params = { period }; if (loc !== "all") params.location_id = loc;
    api.get(ep, { params }).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    if (tab === "pnl") {
      setComp(null);
      api.get("/reports/statements/pnl-comparative", { params: { period } }).then(setComp).catch(() => setComp(null));
    }
  }, [tab, period, loc]);
  useEffect(() => { load(); }, [load]);

  const doExport = (fmt) => {
    const params = { period }; if (loc !== "all") params.location_id = loc;
    downloadReport(`/reports/hub/exports/${tab}.${fmt}`, `${tab}-${period}.${fmt}`, params);
  };

  const body = () => {
    if (loading) return <div className="flex items-center gap-2 p-8 text-sm" style={{ color: C.muted }}><Loader2 className="animate-spin" size={16} /> Loading…</div>;
    if (!data) return <div className="p-8 text-sm" style={{ color: C.muted }}>No data.</div>;

    if (tab === "pnl") {
      if (!data.cogs) return <div className="p-8 text-sm" style={{ color: C.muted }}>Loading…</div>;
      const cols = comp?.columns || [];
      return (
        <div className="grid lg:grid-cols-[1fr_300px] gap-5">
          <div className="rounded-2xl border overflow-x-auto" style={{ borderColor: C.border, background: "#fff" }} data-testid="pnl-statement">
            <div className="px-3 py-2 font-brand font-bold" style={{ background: C.burg, color: "#fff" }}>Statement of Trading, Profit or Loss — {period}</div>
            {comp && cols.length ? (
              <table className="w-full text-xs tabular-nums" data-testid="pnl-comparative">
                <thead>
                  <tr style={{ background: C.canvas }}>
                    <th className="px-3 py-2 text-left" style={{ color: C.text }}>Line Item</th>
                    {cols.map((c) => <th key={c.key} className="px-3 py-2 text-right" style={{ color: C.burg }} data-testid={`pnl-col-${c.key}`}>{c.title}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {comp.rows.map((r, i) => {
                    const isSection = r.kind === "section";
                    const isBold = r.bold || r.kind === "gross" || r.kind === "net";
                    const color = r.kind === "gross" ? C.green : r.kind === "net" ? (r.values && r.values.consolidated >= 0 ? C.green : C.red) : C.text;
                    return (
                      <tr key={i} style={{ background: isBold ? "#FDF6E3" : isSection ? C.canvas : "transparent", borderBottom: `1px solid ${C.border}` }}>
                        <td className="px-3 py-1.5" style={{ paddingLeft: r.indent ? 24 : 12, fontWeight: isBold || isSection ? 700 : 400, color }}>{r.label}</td>
                        {cols.map((c) => (
                          <td key={c.key} className="px-3 py-1.5 text-right" style={{ fontWeight: isBold ? 700 : 500, color }}>
                            {r.values ? n(r.values[c.key]) : ""}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-sm" style={{ color: C.muted }}>Loading comparative columns…</div>
            )}
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Card label="Gross Margin" value={`${data.gross_margin_pct}%`} />
              <Card label="Net Margin" value={`${data.net_margin_pct}%`} />
            </div>
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: C.border, background: "#fff" }} data-testid="pnl-branches">
              <div className="px-3 py-2 font-semibold text-sm" style={{ background: C.canvas, color: C.text }}>All branches — Net Profit</div>
              {(data.branches || []).map((b) => <Row key={b.location_id} label={b.name} value={n(b.net)} color={b.net >= 0 ? C.green : C.red} />)}
              {!(data.branches || []).length && <div className="p-3 text-sm" style={{ color: C.muted }}>No branch activity.</div>}
            </div>
          </div>
        </div>
      );
    }

    if (tab === "funds-release") {
      return (
        <div className="rounded-xl border overflow-x-auto" style={{ borderColor: C.border }} data-testid="funds-table">
          <table className="w-full text-xs tabular-nums">
            <thead><tr style={{ background: C.burg, color: "#fff" }}>{["Date", "Reference", "Source", "Description", "Amount"].map((h, i) => <th key={i} className={`px-3 py-2 ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
            <tbody>
              {(data.rows || []).map((r, i) => <tr key={i} style={{ background: i % 2 ? C.canvas : "#fff" }}>
                <td className="px-3 py-1.5">{r.date}</td><td className="px-3 py-1.5">{r.reference}</td><td className="px-3 py-1.5">{r.source}</td>
                <td className="px-3 py-1.5">{r.description}</td><td className="px-3 py-1.5 text-right">{n(r.amount)}</td></tr>)}
              <tr style={{ background: "#FDF6E3", fontWeight: 700 }}><td className="px-3 py-2" colSpan={4}>Total Inflow</td><td className="px-3 py-2 text-right" style={{ color: C.burg }}>{n(data.total_inflow)}</td></tr>
            </tbody>
          </table>
          {!(data.rows || []).length && <div className="p-4 text-sm" style={{ color: C.muted }}>No operational cash inflows for this period.</div>}
        </div>
      );
    }

    if (tab === "revenue") {
      if (!data.channels) return <div className="p-8 text-sm" style={{ color: C.muted }}>Loading…</div>;
      return <div className="space-y-3">
        <Card label="Monthly Gross Revenue" value={n(data.grand_total)} big />
        <div className="rounded-xl border" style={{ borderColor: C.border }}>
          {data.channels.map((c) => <Row key={c.key} label={c.label} value={n(data.channel_total[c.key])} />)}
          <Row label="Grand Total" value={n(data.grand_total)} bold />
        </div>
        <p className="text-xs" style={{ color: C.muted }}>Full Days 1–31 matrix available in export (CSV/PDF) and in the Reports Hub.</p>
      </div>;
    }

    if (tab === "inventory") {
      if (!Array.isArray(data.rows)) return <div className="p-8 text-sm" style={{ color: C.muted }}>Loading…</div>;
      const closeTotal = data.rows.reduce((s, r) => s + (r.close_v || 0), 0);
      return <div className="space-y-3">
        <Card label="Total Closing Stock Valuation" value={n(closeTotal)} big />
        <div className="rounded-xl border overflow-x-auto" style={{ borderColor: C.border }}>
          <table className="w-full text-xs tabular-nums"><thead><tr style={{ background: C.burg, color: "#fff" }}>{["Item", "Open Val", "Purch Val", "Issued Val", "Close Val"].map((h, i) => <th key={i} className={`px-3 py-2 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
            <tbody>{data.rows.map((r) => <tr key={r.product_id} style={{ borderBottom: `1px solid ${C.border}` }}><td className="px-3 py-1.5">{r.name}</td><td className="px-3 py-1.5 text-right">{n(r.open_v)}</td><td className="px-3 py-1.5 text-right">{n(r.pur_v)}</td><td className="px-3 py-1.5 text-right">{n(r.iss_v)}</td><td className="px-3 py-1.5 text-right">{n(r.close_v)}</td></tr>)}</tbody></table>
        </div>
      </div>;
    }

    if (tab === "expenses") {
      if (!Array.isArray(data.rows)) return <div className="p-8 text-sm" style={{ color: C.muted }}>Loading…</div>;
      return <div className="space-y-3">
        <Card label="Monthly Expenditure" value={n(data.grand_total)} big />
        <div className="rounded-xl border" style={{ borderColor: C.border }}>
          {data.rows.map((r) => <Row key={r.code} label={r.label} value={n(r.total)} />)}
          <Row label="Total" value={n(data.grand_total)} bold />
        </div>
      </div>;
    }
    return null;
  };

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="statements-hub">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border p-3" style={{ borderColor: C.border, background: "#FFFDF9" }}>
        <div><label className="text-[10px] font-bold tracking-widest block mb-1" style={{ color: C.muted }}>MONTH</label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-10 w-44" data-testid="st-period" /></div>
        <div><label className="text-[10px] font-bold tracking-widest block mb-1" style={{ color: C.muted }}>BRANCH</label>
          <Select value={loc} onValueChange={setLoc}><SelectTrigger className="h-10 w-48" data-testid="st-branch"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Consolidated (All)</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => doExport("csv")} data-testid="st-export-csv"><FileDown size={15} className="mr-1" /> CSV</Button>
        <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="st-export-xlsx" style={{ borderColor: C.gold, color: C.burg }}><FileSpreadsheet size={15} className="mr-1" /> Excel</Button>
        <Button onClick={() => doExport("pdf")} style={{ background: C.burg }} className="text-white" data-testid="st-export-pdf"><FileText size={15} className="mr-1" /> PDF</Button>
      </div>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setData(null); }}>
        <TabsList className="flex-wrap h-auto">{TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`st-tab-${t.key}`}>{t.label}</TabsTrigger>)}</TabsList>
        {TABS.map((t) => <TabsContent key={t.key} value={t.key}>{tab === t.key && body()}</TabsContent>)}
      </Tabs>
    </div>
  );
}

function Card({ label, value, big }) {
  return (
    <div className="rounded-xl border px-4 py-3" style={{ borderColor: "#EFE4D0", background: "#FFFDF9" }}>
      <div className="text-[10px] font-bold tracking-widest" style={{ color: "#8B7355" }}>{label.toUpperCase()}</div>
      <div className={`font-brand font-bold ${big ? "text-2xl" : "text-lg"}`} style={{ color: "#8B1E1E" }}>{value}</div>
    </div>
  );
}
